/**
 * CSV generation, download, and the batched fetch that makes export honest.
 *
 * The batching is not an optimisation — it is a correctness requirement.
 * `supabase/config.toml` sets `max_rows = 1000`, which caps EVERY PostgREST
 * response, not just paged ones. A naive "fetch all matching rows, then write
 * the file" returns 1000 rows and no error, so an operator exporting 8,412
 * bookings gets 1,000 and reconciles their month against it. That is the exact
 * class of silent truncation this whole feature exists to fix.
 */

import Papa from 'papaparse'
import { resolveColumns, sanitizeCsvCell, type ExportRow } from '@limo/shared'
import type { ReportResource } from '@limo/shared'

/** PostgREST's per-request row cap (supabase/config.toml: max_rows). */
export const MAX_ROWS_PER_REQUEST = 1000

/**
 * Upper bound on a single export.
 *
 * Chosen so a runaway export can't exhaust browser memory or hold the UI for
 * minutes. When it is hit the caller MUST tell the user the file is partial —
 * see `ExportResult.truncated`. A silent stop here would recreate the bug above.
 */
export const MAX_EXPORT_ROWS = 50_000

// =============================================================================
// CSV writing
// =============================================================================

/**
 * Render rows to CSV text.
 *
 * Uses `Papa.unparse` rather than hand-rolled joining so quoting, embedded
 * commas, quotes and newlines are handled correctly — addresses and
 * `special_requests` routinely contain all three.
 *
 * Columns are resolved through the shared registry, so the header order always
 * matches the order shown in the column picker.
 */
export function toCsv(
  resource: ReportResource,
  rows: readonly ExportRow[],
  selectedColumnKeys: readonly string[]
): string {
  const columns = resolveColumns(resource, selectedColumnKeys)

  if (columns.length === 0) {
    return ''
  }

  const data = rows.map((row) =>
    columns.map((column) => sanitizeCsvCell(column.accessor(row)))
  )

  return Papa.unparse(
    {
      fields: columns.map((column) => column.label),
      data,
    },
    { newline: '\r\n' } // RFC 4180; keeps Excel on Windows happy.
  )
}

/**
 * Render an explicit header row + cell matrix to CSV.
 *
 * For callers that build their own columns rather than using the registry (the
 * Leads pages). Same quoting and injection guards as `toCsv` — the point of
 * sharing this is that the hand-rolled
 * `row.map(c => \`"${String(c).replace(/"/g,'""')}"\`).join(',')` version those
 * pages used got embedded newlines wrong and did nothing about formula injection.
 */
export function rowsToCsv(
  headers: readonly string[],
  rows: readonly (readonly unknown[])[]
): string {
  return Papa.unparse(
    {
      fields: [...headers],
      data: rows.map((row) => row.map(sanitizeCsvCell)),
    },
    { newline: '\r\n' }
  )
}

/** Trigger a browser download of `csv` as `filename`. */
export function downloadCsv(filename: string, csv: string): void {
  // Leading BOM so Excel detects UTF-8 and renders accented customer names
  // correctly. Written as an escape, not a literal, so it stays visible in diffs.
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  try {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } finally {
    // Always release, even if click() threw — otherwise the blob leaks for the
    // lifetime of the document.
    URL.revokeObjectURL(url)
  }
}

/** `bookings-2026-08-05.csv` — date included so repeat exports don't collide. */
export function exportFilename(resource: ReportResource, now: Date = new Date()): string {
  const stamp = now.toISOString().slice(0, 10)
  return `${resource}-${stamp}.csv`
}

// =============================================================================
// Batched fetch
// =============================================================================

export interface ExportProgress {
  readonly fetched: number
  readonly total: number | null
}

export interface ExportResult<T> {
  readonly rows: readonly T[]
  /** True when MAX_EXPORT_ROWS stopped us before the result set was exhausted. */
  readonly truncated: boolean
  /** Server-reported total for the filter set, when available. */
  readonly total: number | null
}

/**
 * A page fetcher: given a half-open row window, return that slice.
 *
 * Callers pass a closure over the SAME query builder the table uses, which is
 * what guarantees the export matches the on-screen filters.
 */
export type RangeFetcher<T> = (
  from: number,
  to: number
) => Promise<{ data: T[] | null; count: number | null; error: { message: string } | null }>

/**
 * Fetch every row matching the current filters, in `MAX_ROWS_PER_REQUEST`
 * batches.
 *
 * Stops when a batch comes back short (the natural end), when the server-
 * reported total is reached, or at `MAX_EXPORT_ROWS` — the last of which sets
 * `truncated` so the UI can say so out loud.
 *
 * Throws on a failed batch rather than returning what it has: a partial file
 * presented as complete is worse than a visible failure.
 */
export async function fetchAllForExport<T>(
  fetchRange: RangeFetcher<T>,
  onProgress?: (progress: ExportProgress) => void
): Promise<ExportResult<T>> {
  const rows: T[] = []
  let offset = 0
  let total: number | null = null

  for (;;) {
    const remainingBudget = MAX_EXPORT_ROWS - rows.length
    if (remainingBudget <= 0) {
      // Hit the cap. Whether more rows exist decides `truncated` below.
      break
    }

    const batchSize = Math.min(MAX_ROWS_PER_REQUEST, remainingBudget)
    const { data, count, error } = await fetchRange(offset, offset + batchSize - 1)

    if (error) {
      throw new Error(`Export failed after ${rows.length} rows: ${error.message}`)
    }

    if (count !== null && count !== undefined) {
      total = count
    }

    const batch = data ?? []
    rows.push(...batch)
    onProgress?.({ fetched: rows.length, total })

    // A short batch means we reached the end of the result set. This is the
    // primary termination condition and the reason the loop exists at all: a
    // full batch is indistinguishable from "there is more", because max_rows
    // truncates without signalling.
    if (batch.length < batchSize) break

    // Belt and braces: stop if the server total says we're done, so a result
    // set that is an exact multiple of the batch size doesn't cost an extra
    // empty round-trip.
    if (total !== null && rows.length >= total) break

    offset += batch.length
  }

  const truncated = total !== null ? rows.length < total : rows.length >= MAX_EXPORT_ROWS

  return { rows, truncated, total }
}
